export type FixedMessageCategory = 'stream' | 'system';

export interface FixedMessageDefault {
  key: string;
  name: string;
  description: string;
  source: string;
  priority: number;
  category: FixedMessageCategory;
}

export const FATAL_ERROR_DEFAULT_SOURCE =
  "Sorry, we're temporarily unable to respond. Please try again later";

/** SSE stream status names (event: status, data.name) + system fixed responses. */
export const FIXED_MESSAGE_DEFAULTS: FixedMessageDefault[] = [
  {
    key: 'processing',
    name: 'Processing',
    description:
      'Shown while the bot starts handling the user message (SSE status event).',
    source: 'Processing your request…',
    priority: 10,
    category: 'stream',
  },
  {
    key: 'searching',
    name: 'Searching',
    description:
      'Shown while knowledge-base or product search tools are running.',
    source: 'Searching for information…',
    priority: 11,
    category: 'stream',
  },
  {
    key: 'calling_tool',
    name: 'Calling tool',
    description: 'Shown while a function/tool is being invoked.',
    source: 'Running a tool…',
    priority: 12,
    category: 'stream',
  },
  {
    key: 'generating_response',
    name: 'Generating response',
    description: 'Shown while the model is composing the reply.',
    source: 'Generating response…',
    priority: 13,
    category: 'stream',
  },
  {
    key: 'waiting_for_data',
    name: 'Waiting for data',
    description: 'Shown while waiting for an external tool or API result.',
    source: 'Waiting for data…',
    priority: 14,
    category: 'stream',
  },
  {
    key: 'fatal_error',
    name: 'Fatal Error',
    description:
      'Returned when the conversation cannot continue because of infrastructure or platform issues.',
    source: FATAL_ERROR_DEFAULT_SOURCE,
    priority: 1,
    category: 'system',
  },
  {
    key: 'quota_reached',
    name: 'Message quota reached',
    description:
      'Returned when the workspace message quota has been exhausted.',
    source:
      'Sorry, you have reached your message limit for this period. Please try again later.',
    priority: 2,
    category: 'system',
  },
  {
    key: 'call_minutes_reached',
    name: 'Call minutes reached',
    description:
      'Returned when the callbot call-minute quota has been exhausted.',
    source: 'Service temporarily unavailable. Please try again later.',
    priority: 3,
    category: 'system',
  },
  {
    key: 'maintenance',
    name: 'Maintenance mode',
    description:
      'Returned when the bot or platform is temporarily under maintenance.',
    source: 'We are currently performing maintenance. Please try again later.',
    priority: 4,
    category: 'system',
  },
];

export const FIXED_MESSAGE_CATEGORY_BY_KEY = new Map(
  FIXED_MESSAGE_DEFAULTS.map((def) => [def.key, def.category]),
);

const FIXED_MESSAGE_FR_SOURCES: Record<string, string> = {
  processing: 'Traitement de votre demande…',
  searching: 'Recherche d\'informations…',
  calling_tool: 'Exécution d\'un outil…',
  generating_response: 'Génération de la réponse…',
  waiting_for_data: 'En attente de données…',
  fatal_error:
    'Désolé, nous ne pouvons pas répondre pour le moment. Veuillez réessayer plus tard.',
  quota_reached:
    'Désolé, vous avez atteint votre limite de messages pour cette période. Veuillez réessayer plus tard.',
  call_minutes_reached:
    'Service temporairement indisponible. Veuillez réessayer plus tard.',
  maintenance:
    'Nous effectuons actuellement une maintenance. Veuillez réessayer plus tard.',
};

export function getFixedMessageDefaultSource(
  def: FixedMessageDefault,
  langCode?: string | null,
): string {
  const lang = String(langCode || 'en').split('-')[0].toLowerCase();
  if (lang === 'fr' && FIXED_MESSAGE_FR_SOURCES[def.key]) {
    return FIXED_MESSAGE_FR_SOURCES[def.key];
  }
  return def.source;
}
